<template>
  <div v-if="isOpen" ref="modalRef" class="absolute h-0 w-0" @keydown.esc="closeModal">
    <div class="fixed inset-0 z-40 bg-gray-900 bg-opacity-50">
      <div tabindex="-1" class="widget-base-modal">
        <Transition name="slide-up" appear>
          <div
            role="dialog"
            v-if="isOpen"
            class="relative h-full w-full p-4 md:h-auto"
            :class="`${modalSizeClasses[modalSize]}`"
          >
            <!-- Modal content -->
            <div
              ref="modalContent"
              class="relative bg-background shadow-custom"
              :class="[{ 'md:rounded-custom': modalSize !== 'full' }, modalSizeClasses[modalSize]]"
            >
              <!-- Modal header -->
              <div v-if="$slots.header" class="widget-base-modal_header">
                <slot name="header" />
                <WButton type="ghost" class="widget-base-modal_close_btn" @click="closeModal">
                  <slot name="close-icon">
                    <WidgetIcon :icon="themeIcons.close" height="24px" class="widget-secondary-icon" />
                  </slot>
                </WButton>
              </div>
              <!-- Modal body -->
              <div
                class="modal-body my-6 overflow-y-auto px-6"
                :class="[
                  $slots.header ? '' : 'pt-0',
                  bodyClass,
                  { mobile: isMobile || isTablet },
                  { 'has-footer': !!$slots.footer },
                ]"
              >
                <slot />
              </div>
              <!-- Modal footer -->
              <div v-if="$slots.footer" class="rounded-b-custom p-6">
                <slot name="footer" />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, watch } from 'vue';
  import { themeIcons } from 'src/utils/config';
  import { useBreakpoints } from 'src/utils/helpers';
  import { useScrollLock } from 'src/composables/useScrollLock';
  import WidgetIcon from './WidgetIcon.vue';
  import WButton from './Button.vue';

  const props = withDefaults(
    defineProps<{
      modelValue: boolean;
      size?: keyof typeof modalSizeClasses;
      bodyClass?: string;
    }>(),
    {
      size: 'md',
      modelValue: false,
      bodyClass: '',
    }
  );

  const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'update:modelValue', isOpen: boolean): void;
  }>();

  const modalRef = ref<HTMLElement>();
  const { isMobile, isTablet } = useBreakpoints();
  const isOpen = computed({
    get() {
      return props.modelValue;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });
  const modalContent = ref<HTMLDivElement | null>(null);

  useScrollLock(isOpen, modalRef.value);

  watch(
    isOpen,
    (value) => {
      if (typeof window === 'undefined') return;
      requestAnimationFrame(() => {
        if (value) {
          if (!modalContent.value) return;
          modalContent.value.tabIndex = 1;
          modalContent.value.focus();
        }
      });
    },
    { immediate: true }
  );

  const modalSize = computed(() => {
    if (isMobile.value || isTablet.value) {
      return 'full';
    }
    return props.size;
  });

  const modalSizeClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'w-screen h-full !p-0',
  };
  function closeModal() {
    emit('close');
    isOpen.value = false;
  }
</script>
